import type { ApiErrorBody, ApiErrorCode } from "@nudgebox/contracts";

/** Error thrown for any non-2xx response. Branch on `code`, not `message`. */
export class ApiClientError extends Error {
  readonly status: number;
  readonly code: ApiErrorCode | "network_error";
  readonly issues: { path: string; message: string }[];
  readonly requestId: string | undefined;

  constructor(init: {
    status: number;
    code: ApiErrorCode | "network_error";
    message: string;
    issues?: { path: string; message: string }[] | undefined;
    requestId?: string | undefined;
  }) {
    super(init.message);
    this.name = "ApiClientError";
    this.status = init.status;
    this.code = init.code;
    this.issues = init.issues ?? [];
    this.requestId = init.requestId;
  }
}

export function isApiClientError(error: unknown): error is ApiClientError {
  return error instanceof ApiClientError;
}

export type Query = Record<string, string | number | boolean | null | undefined>;

export interface RequestOptions {
  query?: Query | undefined;
  body?: unknown;
  headers?: Record<string, string> | undefined;
  signal?: AbortSignal | undefined;
}

export interface HttpClientOptions {
  /** Defaults to same-origin (`""`). */
  baseUrl?: string;
  fetch?: typeof fetch;
}

function buildUrl(baseUrl: string, path: string, query?: Query): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined && value !== null && value !== "") search.set(key, String(value));
  }
  const qs = search.toString();
  return `${baseUrl}${path}${qs ? `?${qs}` : ""}`;
}

export function createHttpClient(options: HttpClientOptions = {}) {
  const baseUrl = (options.baseUrl ?? "").replace(/\/+$/, "");
  const doFetch = options.fetch ?? ((input: RequestInfo | URL, init?: RequestInit) => globalThis.fetch(input, init));

  async function request<T>(method: string, path: string, init: RequestOptions = {}): Promise<T> {
    const headers: Record<string, string> = { accept: "application/json", ...init.headers };
    if (init.body !== undefined) headers["content-type"] = "application/json";

    let response: Response;
    try {
      response = await doFetch(buildUrl(baseUrl, path, init.query), {
        method,
        headers,
        body: init.body !== undefined ? JSON.stringify(init.body) : null,
        credentials: "same-origin",
        signal: init.signal ?? null,
      });
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") throw error;
      throw new ApiClientError({ status: 0, code: "network_error", message: "Network error. Check your connection." });
    }

    if (response.status === 204) return undefined as T;

    const text = await response.text();
    const payload: unknown = text ? safeJson(text) : null;

    if (!response.ok) {
      const body = payload as Partial<ApiErrorBody> | null;
      throw new ApiClientError({
        status: response.status,
        code: body?.error?.code ?? (response.status >= 500 ? "internal_error" : "invalid_request"),
        message: body?.error?.message ?? `Request failed with status ${response.status}.`,
        issues: body?.error?.issues,
        requestId: body?.error?.requestId ?? response.headers.get("x-request-id") ?? undefined,
      });
    }
    return payload as T;
  }

  return {
    get: <T>(path: string, init?: RequestOptions) => request<T>("GET", path, init),
    post: <T>(path: string, init?: RequestOptions) => request<T>("POST", path, init),
    patch: <T>(path: string, init?: RequestOptions) => request<T>("PATCH", path, init),
    delete: <T = void>(path: string, init?: RequestOptions) => request<T>("DELETE", path, init),
  };
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

export type HttpClient = ReturnType<typeof createHttpClient>;
